import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { observer, inject } from 'mobx-react'
import get from 'lodash/get'

import Section from '@client/components/Section'
import Placeholder from '@client/components/Placeholder/index'
import AppModel from '@client/components/App/model'
import EditorModel from '@client/components/Editor/model'
import { 
  FORM_LAYOUT_SHADOW_OPTIONS,
  FORM_SLIDE_IN_LAYOUT_SHADOW_OPTIONS,
} from '@client/components/Editor/settings'
import ItemPlaceholder from '@client/components/EmptyItemPlaceholder'
import PreHeader from '@client/components/PreHeader'
import RequiredLinksHeader from '@client/components/RequiredLinksHeader'
import RequiredLinksFooter from '@client/components/RequiredLinksFooter'
import {
  ITEM_TYPE_MAIL,
  ITEM_TYPE_CONFIRMATION_MAIL,
  ITEM_TYPE_RESPONDER,
  ITEM_TYPE_FORM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  WIZARD_TYPE_PAGE,
} from '@client/components/Editor/settings'
import Ads from '@client/components/Ads'
import {
  NODE_ENV,
  GIT_COMMIT,
  APP_CONFIG_APP_ENV,
  APP_CONFIG_JSFORMS_GANSUB_BASE_URL,
} from '@config/app.config'

import './inlineStyles.scss' // apply inline in production

const Item = forwardRef(({ app, editor, serverRender = false }, ref) => {
  const { columns, settings } = editor
  const bodyStyles = {
    background: editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM 
      ? 'none transparent' 
      : settings.values.backgroundColor,
    padding: '0',
    margin: '0 !important',
    msoLineHeightRule: 'exactly',
  }

  const column = Array.isArray(columns) && columns.length
    ? columns[0]
    : undefined

  if (!column) {
    return null
  }

  const shadowOptions = editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM
    ? FORM_SLIDE_IN_LAYOUT_SHADOW_OPTIONS
    : FORM_LAYOUT_SHADOW_OPTIONS

  return (
    <Choose>
      <When condition={!serverRender}>
        <div
          className={`${
            editor.kind ? editor.kind.toLowerCase() : 'mail'
          }-item-container ${
            column.classNames(app.draggedComponent.type)
          }`}
          style={
            editor.kind === ITEM_TYPE_FORM
              ? {
                  // Mobile view clamps the form to 375px via CSS — leaving
                  // popupWidth here would override that and crop content.
                  width:
                    editor.viewMode !== 'mobile' && editor.popupWidth
                      ? `${editor.popupWidth}px`
                      : undefined,
                  borderRadius: settings.values.popup_borderRadius
                    ? `${settings.values.popup_borderRadius}px`
                    : undefined,
                  boxShadow: (
                    shadowOptions.find(
                      (item) => item.value === settings.values.popup_shadow
                    ) || {}
                  ).shadowValue,
                  overflow: 'hidden',
                }
              : undefined
          }
          ref={ref}
        >
          {(app.wizard.type === WIZARD_TYPE_PAGE && (
            (editor.kind === ITEM_TYPE_PAGE ||
              editor.kind === ITEM_TYPE_LANDING_PAGE ||
              editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) && (
                <style>
                  {`
                  * {
                    box-sizing: border-box;
                  }
                  .${
                    editor.kind ? editor.kind.toLowerCase() : 'mail'
                  }-item-container {
                    margin: 0;
                    display: grid;
                    line-height: 1.6;
                    flex-direction: column;
                    align-items: center;
                    font-family: sans-serif;
                  }
                  h2 {
                    margin: 0;
                    font-size: 18px;
                  }
                  .padding {
                    padding: 40px;
                  }
                  .content-width-margin {
                    margin: 0 20px;
                  }
                  .content-background {
                    background-color: ${settings.values.contentColor};
                  }
                  .min-height {
                    min-height: 5px;
                  }
                  .max-width-sm {
                    max-width: 600px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .max-width {
                    max-width: 900px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .max-width-lg {
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .one-col {
                    display: flex;
                  }
                  .two-col {
                    display: flex;
                    gap: 40px;
                  }
                  .relative {
                    position: relative;
                  }

                  @media (max-width: 800px) {
                    .padding {
                      padding: 20px !important;
                    }
                    .two-col {
                      flex-direction: row;
                    }
                    .two-col.stack {
                      flex-direction: column;
                      gap: 20px;
                    }

                    .two-col .column.stack {
                      max-width:100% !important;
                      width: 100% !important;
                    }
                  }
                `}
                </style>
              )
          ))}
          <PreHeader kind={editor.kind} preHeader={editor.pre_header} serverRender={serverRender} />
          {editor.kind === ITEM_TYPE_SURVEY ? (
            <form
              method="POST"
              action={`${
                APP_CONFIG_JSFORMS_GANSUB_BASE_URL
              }/surveys/${editor.itemHash}/`}
              encType="multipart/form-data"
            >
              <For each="item" index="index" of={column.items}>
                <Section
                  model={item}
                  container={column}
                  key={item.id}
                  index={index}
                  count={column.items.length}
                />
              </For>
              <input
                type="hidden"
                name="csrfmiddlewaretoken"
                id="csrfmiddlewaretoken"
              />
            </form>
          ) : (
            <For each="item" index="index" of={column.items}>
              <Section
                model={item}
                container={column}
                key={item.id}
                index={index}
                count={column.items.length}
              />
            </For>
          )}
          <Placeholder position={column.placeholderPosition} />
          <ItemPlaceholder itemsCount={column.items.length} />
        </div>
      </When>
      <When condition={serverRender}>
        <body
          width={editor.form_type === 'popup' 
                  ? "100%"
                  : undefined}
          style={bodyStyles}
          data-wizard-type={app.wizard.type || ''}
          data-user-id={get(app, 'user.id') || ''}
          data-type={editor.kind || ''}
          data-sub-type={editor.subKind || ''}
          data-id={editor.id || ''}
          data-hash={editor.itemHash || ''}
          data-release={GIT_COMMIT || ''}
          data-env={NODE_ENV || ''}
          data-app-env={APP_CONFIG_APP_ENV || ''}
        >
          {editor.kind === ITEM_TYPE_FORM && (
            <style>
              {`
              html,
              body {
                ${editor.form_type === 'popup' 
                  ? 
                  'height: 100%;' 
                  : `width: 100%;`}
              }

              *, :after, :before {
                box-sizing: border-box;
              }

              * {
                padding: 0;
                margin: 0
              }

              @keyframes gan-fadeInUp {
                0% {
                  transform: translateY(100%);
                  opacity: 0;
                }
                100% {
                  transform: translateY(0%);
                  opacity: 1;
                }
              }

              .gan-fadeInUp-animation {
                animation: 1.5s gan-fadeInUp forwards;
              }

              body {
                font-size: 16px;
                font-family: Arial
              }

              .mb0 {
                margin-bottom: 0!important
              }

              .mb05 {
                margin-bottom: 5px!important
              }

              .mb10 {
                margin-bottom: 10px!important
              }

              .mb15 {
                margin-bottom: 15px!important
              }

              .mb20 {
                margin-bottom: 20px!important
              }

              .mb30 {
                margin-bottom: 30px!important
              }

              .mt0 {
                margin-top: 0!important
              }

              .mt15 {
                margin-top: 15px!important
              }

              .gan-container {
                box-sizing: border-box;
                display: table;
                position: static;
                ${editor.form_type === 'popup' 
                  ? 
                  `width: 100%!important;
                  height: 100%;
                  margin: 0 auto;` 
                  : ''}
              }

              .description p {
                margin: 0 0 10px 0
              }

              .description p:last-child {
                margin: 0
              }

              .description a:hover {
                text-decoration: none
              }

              .gan-confirmation-text input {
                line-height: 20px
              }

              .form-group .form-control,
              .input-group .form-control {
                height: auto;
                box-shadow: none
              }

              .form-control:focus {
                box-shadow: none;
                z-index: 1
              }

              .overlay {
                position: fixed;
                left: 0;
                top: 0;
                z-index: 9999;
                width: 100%;
                height: 100%;
                overflow: auto;
                overflow-x: hidden;
                cursor: default
              }

              .ganPopupConfirmation.checkbox {
                margin: 0
              }

              .ganPopupConfirmation.checkbox p:last-child {
                margin: 0
              }

              .ganPopupConfirmation.checkbox a {
                margin: 0
              }
              `}
            </style>
          )}
          {editor.kind === ITEM_TYPE_FORM && (
            <style>
              {`
              .gan-vertical-align-center {
                display: table-cell;
                vertical-align: middle;
                text-align: ${editor.form_type === 'slide_in' ? 'right' : 'center'}
              }

              .gan-popup {
                position: relative;
                display: inline-block;
                margin: 10px;
                padding: 0;
                max-width: ${editor.popupWidth || 0}px;
                background-color: #fff;
                ${editor.form_type === 'slide_in' ? 'opacity: 0; transform: translateY(100%);' : ''}
              }

              .form-sidebar {
                float: left;
                height: 500px;
                width: 250px
              }

              .form-content {
                float: left;
                display: table;
                height: 500px;
                padding: 30px 30px 30px 30px;
                width: 450px
              }

              .form-content .form-content-vertical-align {
                display: table-cell;
                vertical-align: middle
              }

              .gan-popup .form-group .form-control,
              .gan-popup .input-group .form-control {
                background: #fff;
                color: #333;
                font-size: 16px;
                line-height: 20px;
                padding: 15px 15px
              }

              .gan-popup .input-group .form-control {
                border-color: #ccc;
                border-radius: 6px 0 0 6px;
                border-style: solid;
                border-width: 1px 1px 1px 1px;
                z-index: 3
              }

              .gan-popup .form-group .form-control {
                border-color: #ccc;
                border-radius: 6px;
                border-style: solid;
                border-width: 1px
              }

              .gan-popup .btn {
                background: #e74c3c;
                border: none;
                color: #fff;
                font-size: 16px;
                font-weight: 700;
                line-height: 20px;
                padding: 0 30px;
              }

              .gan-popup .btn:active,
              .gan-popup .btn:focus,
              .gan-popup .btn:hover {
                background: #c0392b;
                color: #fff
              }

              .gan-popup .input-group-btn .btn {
                border-color: #ccc;
                border-radius: 0 6px 6px 0!important;
                border-style: solid;
                border-width: 1px 1px 1px 0
              }

              .gan-popup label {
                width: 100%;
                text-align: left;
                color: #666;
                font-weight: 500;
              }

              .gan-popup .gan-confirmation-text {
                color: #666;
                font-size: 13px;
                font-weight: 400;
                line-height: 20px
              }

              .gan-popup .close-popup {
                text-align: center;
                font-size: 16px;
                z-index: 999;
                position:absolute;
                right:10px;
                top: 10px;
                color: #000;
                background: rgba(255, 255, 255, .7);
                padding: 5px;
                border-radius: 100%;
              }

              .gan-popup .close-popup:active,
              .gan-popup .close-popup:focus,
              .gan-popup .close-popup:hover {
                text-decoration: none;
                color: #000;
                outline: 0
              }

              .gan-popup-row .gan-submit .gan-validation-error,
              .gan-popup-row .gan-validate .gan-validation-error {
                display: none;
              }
              .gan-popup-row .gan-submit.gan-error .gan-validation-error,
              .gan-popup-row .gan-validate.gan-error .gan-validation-error {
                display: block;
              }

              @media only screen and (max-width: ${editor.popupWidth}px) {
                html, body {
                  width: 100%!important;
                }
                .gan-container {
                  width: 100%!important;
                  height: 100%!important;
                }
                .gan-popup.popup > div {
                  border-radius: 0!important;
                }
                .gan-popup.popup > div {
                  border-radius: 0!important;
                }

                .gan-column-bg-image {
                  display: none!important;
                }
                .gan-section {
                  width: 100%!important;
                  border-radius: 0!important;
                }
                .gan-column-form {
                  width: 100%!important;
                  max-width: 100%!important;
                  padding: 20px 0!important;
                }

                .gan-popup h3 {
                  font-size: 18px
                }

                .gan-popup .description {
                  font-size: 13px
                }

                .gan-popup .close-text {
                  font-size: 12px
                }

                .gan-popup .form-sidebar {
                  display: none!important
                }

                .gan-popup .form-content {
                  height: auto!important;
                  width: 100%!important;
                  padding: 15px!important
                }

                .gan-popup .gan-popup-row {
                  padding: 0 20px!important;
                }

                .gan-popup .popup-content {
                  margin: 0 15px 15px 15px!important
                }

                .gan-popup .popup-form {
                  margin-bottom: 0!important
                }

                .gan-popup .checkbox,.gan-popup .checkbox label {
                  margin-bottom: 5px!important
                }

                .gan-popup .close-popup {
                  top: 3px!important;
                  right: 3px!important;
                  padding: 2px!important;
                }

                .gan-popup .popup-content .row.mb15 {
                  margin-bottom: 5px!important
                }
              }`}
            </style>
          )}
          {(app.wizard.type === WIZARD_TYPE_PAGE && (
            (editor.kind === ITEM_TYPE_PAGE ||
              editor.kind === ITEM_TYPE_LANDING_PAGE ||
              editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS) && (
                <style>
                  {`
                  * {
                    box-sizing: border-box;
                  }
                  body {
                    margin: 0;
                    display: grid;
                    line-height: 1.6;
                    flex-direction: column;
                    align-items: center;
                    font-family: sans-serif;
                  }
                  h2 {
                    margin: 0;
                    font-size: 18px;
                  }
                  .padding {
                    padding: 40px;
                  }
                  .content-width-margin {
                    margin: 0 20px;
                  }
                  .content-background {
                    background-color: ${settings.values.contentColor};
                  }
                  .min-height {
                    min-height: 5px;
                  }
                  .max-width-sm {
                    max-width: 600px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .max-width {
                    max-width: 900px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .max-width-lg {
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                  }
                  .one-col {
                    display: flex;
                  }
                  .two-col {.two-col .column
                    flex-direction: row;
                  }
                  .two-col.stack {
                    display: flex;
                    gap: 40px;
                  }
                  .relative {
                    position: relative;
                  }

                  @media (max-width: 800px) {
                    .padding {
                      padding: 20px !important;
                    }
                    .two-col {
                      flex-direction: row;
                    }
                    .two-col.stack {
                      flex-direction: column;
                      gap: 20px;
                    }

                    .two-col .column.stack {
                      max-width:100% !important;
                      width: 100% !important;
                    }
                  }
                `}
                </style>
              )
          ))}
          <PreHeader kind={editor.kind} preHeader={editor.pre_header} serverRender={serverRender} />
          {(editor.kind === ITEM_TYPE_MAIL ||
            editor.kind === ITEM_TYPE_CONFIRMATION_MAIL ||
            editor.kind === ITEM_TYPE_RESPONDER) && (
            <table width="100%" border="0" cellPadding="0" cellSpacing="0">
              <tr>
                <td
                  width="100%"
                  valign="top"
                  align="center"
                  bgcolor={bodyStyles.background}
                >
                  {(editor.kind === ITEM_TYPE_MAIL ||
                    editor.kind === ITEM_TYPE_RESPONDER) && (
                    <RequiredLinksHeader />
                  )}
                  <For each="item" index="index" of={column.items}>
                    <Section
                      model={item}
                      container={column}
                      key={item.id}
                      index={index}
                      count={column.items.length}
                    />
                  </For>
                  {(editor.kind === ITEM_TYPE_MAIL ||
                    editor.kind === ITEM_TYPE_RESPONDER) && (
                    <RequiredLinksFooter />
                  )}
                  <Ads />
                </td>
              </tr>
            </table>
          )}
          {editor.kind !== ITEM_TYPE_MAIL &&
            editor.kind !== ITEM_TYPE_CONFIRMATION_MAIL &&
            editor.kind !== ITEM_TYPE_RESPONDER &&
            app.wizard.type !== WIZARD_TYPE_PAGE && (
              <div className="gan-container">
                <div className="gan-vertical-align-center">
                  <div
                    className={`gan-popup ${
                      editor.form_type === 'slide_in' ? 'slide-in' : 'popup'
                    } gan-popup_${`${editor.kind}`.toLowerCase()}`}
                    style={{
                      borderRadius: settings.values.popup_borderRadius
                        ? `${settings.values.popup_borderRadius}px`
                        : undefined,
                      boxShadow: (
                        shadowOptions.find(
                          (item) => item.value === settings.values.popup_shadow
                        ) || {}
                      ).shadowValue,
                      overflow: 'hidden',
                    }}
                  >
                    {editor.kind === ITEM_TYPE_SURVEY ? (
                      <form
                        id="gan-survey-form"
                        method="POST"
                        action={`${
                          APP_CONFIG_JSFORMS_GANSUB_BASE_URL
                        }/surveys/${editor.itemHash}/`}
                        encType="multipart/form-data"
                      >
                        <For each="item" index="index" of={column.items}>
                          <Section
                            model={item}
                            container={column}
                            key={item.id}
                            index={index}
                            count={column.items.length}
                          />
                        </For>
                        <input
                          type="hidden"
                          name="csrfmiddlewaretoken"
                          id="csrfmiddlewaretoken"
                        />
                      </form>
                    ) : (
                      <For each="item" index="index" of={column.items}>
                        <Section
                          model={item}
                          container={column}
                          key={item.id}
                          index={index}
                          count={column.items.length}
                        />
                      </For>
                    )}
                    <Ads />
                  </div>
                </div>
              </div>
            )}
          {(app.wizard.type === WIZARD_TYPE_PAGE && (
            editor.kind === ITEM_TYPE_PAGE ||
            editor.kind === ITEM_TYPE_LANDING_PAGE ||
            editor.kind === ITEM_TYPE_LANDING_PAGE_SUCCESS)) && (
              <>
                <For each="item" index="index" of={column.items}>
                  <Section
                    model={item}
                    container={column}
                    key={item.id}
                    index={index}
                    count={column.items.length}
                  />
                </For>
                <Ads />
              </>
            )}
          {editor.kind === ITEM_TYPE_FORM && (
            <div style={{ display: 'none' }}>
              {editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM && (
                <script
                  dangerouslySetInnerHTML={{
                    __html: `
                    // ganSendSize function
                    // get the width and height of the document
                    // and post the values to the parent frame
                    function ganSendSize() {
                      const elem = document.getElementsByClassName('gan-container')[0]
                      if (!elem) {
                        return
                      }
                      const dimentions = elem.getBoundingClientRect() || {}

                      const sizeParams = JSON.stringify({
                        hash: '${editor.itemHash}',
                        form_type: '${editor.form_type}',
                        popup_width: ${editor.popupWidth || 0},
                        width: dimentions.width || document.documentElement.scrollWidth,
                        height: dimentions.height || document.documentElement.scrollHeight
                      })
                      window.parent.postMessage(
                        'gan' + '|' +
                        'accounts' + '|' +
                        'webforms' + '|' +
                        sizeParams + '|' +
                        'resize',
                        '*'
                      )
                    }

                    function ganOnLoad() {
                      const isIframe = window.self !== window.top
                      const elem = document.getElementsByClassName('gan-popup')[0]
                      if (elem) {
                        if (isIframe) {
                          elem.classList.add('gan-fadeInUp-animation')
                        } else {
                          elem.style.opacity = '1'
                          elem.style.transform = 'none'
                        }
                      }

                      ganSendSize()
                    }

                    // when this window loads or resizes …
                    // call our ganSendSize function
                    window.addEventListener('load', ganOnLoad)
                    window.addEventListener('resize', ganSendSize)
                    `,
                  }}
                />
              )}
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                  function close() {
                    window.parent.postMessage(
                      '${[
                        'gan',
                        'accounts',
                        'webforms',
                        JSON.stringify({ hash: editor.itemHash, form_type: editor.form_type }),
                        'hide',
                      ].join('|')}',
                      '*'
                    )
                  }
                  function gan_webform_success_${`${editor.itemHash}`.replace(
                    /\-/g,
                    ''
                  )}() {
                    // Hide all row-form elements
                    document.querySelectorAll('.row-form').forEach(function(el) {
                      el.style.display = 'none';
                    });
                    
                    // Show all row-success elements
                    document.querySelectorAll('.row-success').forEach(function(el) {
                      el.style.display = '';
                    });
                    
                    // Scroll to top
                    window.scrollTo(0, 0);
                    
                    // Check if success template exists and has content
                    var rowSuccess = document.querySelector('.row-success');
                    var successTemplate = document.querySelector('.success-template');
                    
                    if (rowSuccess && successTemplate && 
                        rowSuccess.querySelectorAll('.success-template').length > 0 && 
                        successTemplate.innerHTML.trim()) {
                      // Replace row-success with success-template content
                      var templateContent = successTemplate.innerHTML;
                      var newElement = document.createElement('div');
                      newElement.innerHTML = templateContent;
                      
                      // Replace the row-success element with the template content
                      while (newElement.firstChild) {
                        rowSuccess.parentNode.insertBefore(newElement.firstChild, rowSuccess);
                      }
                      rowSuccess.parentNode.removeChild(rowSuccess);
                    }
                    
                    ${editor.form_type === 'slide_in' && editor.kind === ITEM_TYPE_FORM 
                      ? 'ganSendSize()' 
                      : ''}
                  }
                  document.onkeydown = function(e) {
                    if ("key"in (e = e || window.event) ? "Escape" == e.key : 27 == e.keyCode) {
                      close()
                    }
                  }
                  `,
                }}
              />
            </div>
          )}
        </body>
      </When>
    </Choose>
  )
})

Item.propTypes = {
  app: PropTypes.instanceOf(AppModel),
  editor: PropTypes.instanceOf(EditorModel),
  serverRender: PropTypes.bool,
}

export default inject('editor', 'app', 'serverRender')(observer(Item))
